import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep } from "lodash";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Checkbox } from "components/ui/checkbox";
import { AuthContext, JobsContext } from "context/providers";
import {
  MATCHER_LABEL,
  formatMatcher,
  prepareListViewLocation,
  prepareListViewRate,
} from "components/UserCardList/helpers/userCard";
import { UpdateRecencyBadge } from "components/UserCardList/components/UpdateRecencyBadge";
import {
  APPLICATION_TAB_NAMES,
  ROWS_PER_PAGE_ON_PAGINATED_TABLE,
} from "lookup";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import { DataTablePagination } from "./Pagination";
import { cn } from "utils";
import AllSkillsModal from "./AllSkillsModal";
import CompactSkillsList from "./CompactSkillsList";
import FullProfileDialog from "../shared/FullProfileDialog";
import TalentCell from "./TalentCell";
import ListViewRowActions from "./ListViewRowActions";
import DownloadSelectedButton from "./DownloadSelectedButton";
import RankDetails from "../MatchAgentListView/HitCard/components/RankDetails";
import MatchScoreBadge from "components/UserCardList/components/MatchScoreBadge";
import Status from "../UserCardResult/molecules/Status";

const getFocusedRowIndex = (rows, activeElement) => {
  if (!activeElement) return -1;

  return rows.findIndex(
    (row) => row === activeElement || row.contains(activeElement)
  );
};

const getListViewRowTabIndex = (focusedRowIndex, rowIndex) => {
  if (focusedRowIndex === -1) {
    return rowIndex === 0 ? 0 : -1;
  }

  return focusedRowIndex === rowIndex ? 0 : -1;
};

const handleListViewRowKeyDown = (e, row, setSelectedHit) => {
  if (e.key !== "Enter" && e.key !== " ") return;

  if (e.target !== e.currentTarget && e.target.closest("button, a")) {
    return;
  }

  e.preventDefault();
  setSelectedHit(row.original);
};

const handleListViewArrowKeyDown = (e, rowRefs, setFocusedRowIndex) => {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

  const rows = rowRefs.current.filter(Boolean);
  if (!rows.length) return;

  const activeElement = document.activeElement;
  if (!e.currentTarget.contains(activeElement)) return;

  const currentIndex = getFocusedRowIndex(rows, activeElement);
  if (currentIndex === -1) return;

  const direction = e.key === "ArrowDown" ? 1 : -1;
  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= rows.length) {
    e.preventDefault();
    return;
  }

  e.preventDefault();
  rows[nextIndex]?.focus();
  rows[nextIndex]?.scrollIntoView({ block: "nearest" });
  setFocusedRowIndex(nextIndex);
};

const CareerTrackAnalysisDialog = ({ hit, ideal, score }) => {
  const { rankDetails } = hit;

  if (rankDetails?.score === undefined) {
    return null;
  }

  return (
    <Dialog>
      <DialogTrigger
        onClick={(e) => e.stopPropagation()}
        className="flex min-w-[110px] flex-col items-start gap-1"
      >
        <MatchScoreBadge ideal={ideal} score={score} showLabel={false} />

        <p className="inline-flex h-9 items-center justify-center whitespace-nowrap text-sm font-semibold text-sky-600 transition-colors hover:text-sky-700 hover:underline">
          See details
        </p>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] w-[88vw] max-w-[1548px] overflow-y-auto rounded-lg border-0 bg-white p-8 font-inter shadow-xl [&>button.absolute]:right-8 [&>button.absolute]:top-8 [&>button.absolute]:rounded-full [&>button.absolute]:bg-slate-500 [&>button.absolute]:p-1 [&>button.absolute]:text-white [&>button.absolute]:opacity-100 [&>button.absolute]:ring-offset-0 [&>button.absolute>svg]:h-4 [&>button.absolute>svg]:w-4">
        <DialogTitle className="sr-only">Career Track Analysis</DialogTitle>

        <RankDetails {...rankDetails} hit={hit} showProfileImage defaultOpen />
      </DialogContent>
    </Dialog>
  );
};

const MatchScoreCell = ({ hit }) => {
  const { ideal, score } = hit.rankDetails || {};

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <CareerTrackAnalysisDialog hit={hit} ideal={ideal} score={score} />
    </div>
  );
};

const prepareItemsForTable = (items) => {
  const preparedItems = items?.map((item) => {
    const clonedHit = cloneDeep(item);

    return {
      ...clonedHit,
      listViewRate: prepareListViewRate(clonedHit),
      listViewLocation: prepareListViewLocation(
        clonedHit.location,
        clonedHit.locale
      ),
    };
  });
  return preparedItems || [];
};

const UserTableListView = ({
  items,
  activeTabName,
  collectionKey,
  onVisibleItemsChange,
  showCareerTrackAnalysis,
}) => {
  const [selectedHit, setSelectedHit] = useState(null);
  const [skillsModalHit, setSkillsModalHit] = useState(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  const [rowSelection, setRowSelection] = useState({});
  const rowRefs = useRef([]);
  const { jobOpp } = useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);
  const canBulkDownload = !!loggedInUser?.canAccessBulkDownload;

  const itemsPreparedForTable = useMemo(
    () => prepareItemsForTable(items),
    [items]
  );

  const tabUsesMatches =
    activeTabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
    activeTabName === APPLICATION_TAB_NAMES.PRECANDIDATES ||
    activeTabName === APPLICATION_TAB_NAMES.MATCHED;

  const showMatcher = activeTabName === APPLICATION_TAB_NAMES.MATCHED;

  const showPagination =
    tabUsesMatches || activeTabName === APPLICATION_TAB_NAMES.LONGLISTED;

  const columns = useMemo(() => {
    const columnList = [
      ...(canBulkDownload
        ? [
            {
              id: "select",
              meta: { className: "w-10 pr-0" },
              header: ({ table }) => (
                <Checkbox
                  aria-label="Select all candidates"
                  checked={
                    table.getIsAllRowsSelected() ||
                    (table.getIsSomeRowsSelected() && "indeterminate")
                  }
                  onCheckedChange={(value) =>
                    table.toggleAllRowsSelected(!!value)
                  }
                />
              ),
              cell: ({ row }) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    aria-label={`Select ${row.original.given_name} ${row.original.family_name}`}
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                  />
                </div>
              ),
            },
          ]
        : []),
      {
        id: "talent",
        header: "Talent",
        cell: ({ row }) => <TalentCell hit={row.original} />,
      },
      ...(showCareerTrackAnalysis
        ? [
            {
              id: "matchScore",
              header: "Match score",
              cell: ({ row }) => {
                return <MatchScoreCell hit={row.original} />;
              },
            },
          ]
        : []),
      {
        id: "skills",
        header: "Skills",
        cell: ({ row }) => (
          <CompactSkillsList
            hit={row.original}
            onSkillClick={() => setSkillsModalHit(row.original)}
            onViewAllClick={() => setSkillsModalHit(row.original)}
          />
        ),
      },
      {
        id: "activity",
        header: "Activity",
        cell: ({ row }) => {
          const hasActivity = !!(
            row.original.lastActivityDate || row.original.createdAt
          );
          return (
            <div className="flex items-center justify-between gap-3 min-w-[320px]">
              <div onClick={(e) => e.stopPropagation()}>
                <UpdateRecencyBadge
                  hit={row.original}
                  variant="listView"
                  isClickable={hasActivity}
                />
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <ListViewRowActions
                  hit={row.original}
                  row={row}
                  activeTabName={activeTabName}
                  collectionKey={collectionKey}
                  tabUsesMatches={tabUsesMatches}
                />
              </div>
            </div>
          );
        },
      },
    ];

    if (tabUsesMatches) {
      columnList.splice(
        (canBulkDownload ? 1 : 0) + 2,
        0,
        {
          id: "matchStatus",
          header: "Status",
          cell: ({ row }) => (
            <Status matchStatus={row.original.match.status} variant="inline" />
          ),
        },
        ...(showMatcher
          ? [
              {
                id: "matcher",
                header: MATCHER_LABEL,
                cell: ({ row }) => (
                  <span className="whitespace-nowrap text-sm">
                    {formatMatcher(row.original.match?.matcher)}
                  </span>
                ),
              },
            ]
          : [])
      );
    }

    return columnList;
  }, [
    activeTabName,
    collectionKey,
    tabUsesMatches,
    showMatcher,
    showCareerTrackAnalysis,
    canBulkDownload,
  ]);

  const table = useReactTable({
    data: itemsPreparedForTable,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    initialState: {
      columnVisibility: {
        finalist: activeTabName === APPLICATION_TAB_NAMES.MATCHED,
      },
      pagination: {
        pageSize: ROWS_PER_PAGE_ON_PAGINATED_TABLE,
        pageIndex: 0,
      },
    },
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const visibleItems = useMemo(
    () => items.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [items, pageIndex, pageSize]
  );

  useEffect(() => {
    onVisibleItemsChange(visibleItems);
  }, [onVisibleItemsChange, visibleItems]);

  useEffect(() => {
    rowRefs.current = [];
    setFocusedRowIndex(-1);
    setRowSelection({});
  }, [items]);

  const checkedHits = table
    .getSelectedRowModel()
    .rows.map((row) => row.original);

  return (
    <div
      className="pb-4"
      onKeyDownCapture={(e) =>
        handleListViewArrowKeyDown(e, rowRefs, setFocusedRowIndex)
      }
    >
      {canBulkDownload && checkedHits.length > 0 && (
        <div className="mb-2 flex justify-end">
          <DownloadSelectedButton
            selectedHits={checkedHits}
            jobTitle={jobOpp?.title}
          />
        </div>
      )}
      <div className="w-full overflow-x-auto">
        <div className="min-w-[1000px]">
          <Table className="w-full" aria-label="Talent list view">
            <TableHeader className="bg-gray-50">
              {table.getHeaderGroups()?.map((headerGroup) => {
                const { headers, id } = headerGroup;
                return (
                  <TableRow
                    key={id}
                    className="bg-gray-50 hover:bg-gray-50 border-b border-gray-200"
                  >
                    {headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            "whitespace-nowrap font-normal text-gray-500 text-sm font-inter",
                            "px-4 py-3 bg-gray-50 border-b border-gray-200 h-auto",
                            header.column.columnDef.meta?.className
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHeader>
            <TableBody className="bg-white">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows?.map((row, rowIndex) => {
                  const visibleCells = row.getVisibleCells();

                  return (
                    <TableRow
                      key={row.id}
                      id={row.id}
                      ref={(element) => {
                        rowRefs.current[rowIndex] = element;
                      }}
                      data-state={row.getIsSelected() && "selected"}
                      tabIndex={getListViewRowTabIndex(
                        focusedRowIndex,
                        rowIndex
                      )}
                      aria-label={`View profile for ${row.original.given_name} ${row.original.family_name}`}
                      className={cn(
                        "user-list-view-row text-black font-inter bg-white",
                        "border-b border-gray-100",
                        "cursor-pointer hover:bg-gray-50 transition-colors",
                        "outline-none focus:bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-gray-400"
                      )}
                      onFocus={() =>
                        setFocusedRowIndex((prev) =>
                          prev === rowIndex ? prev : rowIndex
                        )
                      }
                      onClick={() => setSelectedHit(row.original)}
                      onKeyDown={(e) =>
                        handleListViewRowKeyDown(e, row, setSelectedHit)
                      }
                    >
                      {visibleCells?.map((cell) => {
                        return (
                          <TableCell
                            key={`${cell.id}-${row.original.id}`}
                            id={`${cell.id}-${row.original.id}`}
                            className={cn(
                              "px-4 py-3 align-middle user-list-view-cell",
                              cell.column.columnDef.meta?.className
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length}>No results.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <FullProfileDialog
        open={!!selectedHit}
        onOpenChange={(open) => !open && setSelectedHit(null)}
        hit={selectedHit}
        activeTabName={activeTabName}
        collectionKey={collectionKey}
        showCareerTrackAnalysis={showCareerTrackAnalysis}
      />

      {skillsModalHit && (
        <AllSkillsModal
          hit={skillsModalHit}
          isOpen={!!skillsModalHit}
          onClose={() => setSkillsModalHit(null)}
        />
      )}

      {showPagination ? <DataTablePagination table={table} /> : null}
    </div>
  );
};

export default UserTableListView;
